import { InputAdornment, TextField, TextFieldProps } from '@mui/material'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { removeTrailingZeroes } from '../../../utils/conversion'

interface NumberInputProps extends Omit<TextFieldProps, 'onChange'> {
  max?: number
  step?: number
  value: number | null
  showMaxValue?: boolean
  onChange?: (value: number | null) => void
}

// Format number to string with decimal point (max 2 decimal places)
const formatValue = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return ''
  
  if (Number.isInteger(num)) return num.toString()
  
  const formattedNum = removeTrailingZeroes(num, 2)
  return formattedNum
}

function NumberInput({
  max,
  value,
  onChange,
  step = 0.5,
  showMaxValue = false,
  ...props
}: NumberInputProps) {
  const [displayValue, setDisplayValue] = useState<string>(formatValue(value))

  const parseValue = (str: string): number | null => {
    if (str === '') return null
    return parseFloat(str)
  }

  const roundedMax = useMemo(
    () => (max !== undefined ? Math.round(max * 100) / 100 : undefined),
    [max]
  )

  useEffect(() => {
    if (roundedMax && value !== null && value > roundedMax && onChange) {
      onChange(roundedMax)
    }
  }, [roundedMax, value, onChange])

  useEffect(() => {
    if (parseValue(displayValue) !== value) {
      setDisplayValue(formatValue(value))
    }
  }, [value, displayValue])

  const handleInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    // Firefox performs number input validation differently from other browsers, and doesn't automatically prevent user
    // from entering non-numeric characters. In a situation where user enters a non-numeric character into an empty input,
    // the change event is not fired, and the new character is still displayed in the input.
    // By using the input event, we can catch the invalid input, and then explicitly reset the target value
    // to the previously displayed value.
    if (e.target.validity.badInput) {
      e.target.value = displayValue
    }
  }, [displayValue])

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    
    // Allow digits, and a single decimal point with up to 2 decimal places
    // Regex explanation: Match either empty string, just digits, or digits with a single decimal point followed by up to 2 digits
    if (newValue !== '' && !/^$|^\d+$|^\d+\.\d{0,2}$|^\.\d{0,2}$/.test(newValue)) {
      return
    }
        
    // Validate max if the input is not empty
    if (newValue !== '') {
      const numValue = parseValue(newValue)
      
      if (numValue !== null) {
        if (roundedMax !== undefined && numValue > roundedMax) {
          setDisplayValue(formatValue(roundedMax))
          onChange?.(roundedMax)
        } else {
          // If the new value is within bounds, keep the original format of the entered value for the display value;
          // otherwise you will get the behaviour where user enters 4.0, but it gets immediately replaced by 4.
          setDisplayValue(newValue)
          onChange?.(numValue)
        }
      }
    } else {
      // Empty input
      setDisplayValue('')
      onChange?.(null)
    }
  }, [setDisplayValue, onChange, roundedMax])

  return (
    <TextField
      {...props}
      type="number"
      value={displayValue}
      onInput={handleInput}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
          e.preventDefault()
        }
      }}
      onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
      inputProps={{
        ...props.inputProps,
        min: 0,
        max: roundedMax,
        step,
        inputMode: "numeric"
      }}
      InputProps={{
        ...props.InputProps,
        endAdornment: showMaxValue ? (
          <InputAdornment position="end">
            /
            {roundedMax}
          </InputAdornment>
        ) : undefined,
      }}
    />
  )
}

export default NumberInput